// src/components/BatchDropdown.jsx
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const BatchDropdown = forwardRef(({ options, value, onChange, loading, disabled }, ref) => {
    console.log("🟢 BatchDropdown Props Data:", options);
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const tableRef = useRef(null);

    // ✅ Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (buttonRef.current) {
                buttonRef.current.focus();
            }
        },
        open: () => {
            handleToggle();
        }
    }));

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ✅ Scroll focused row into view
    useEffect(() => {
        if (isOpen && focusedIndex >= 0 && tableRef.current) {
            const rows = tableRef.current.querySelectorAll('tbody tr');
            if (rows[focusedIndex]) {
                rows[focusedIndex].scrollIntoView({ 
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [focusedIndex, isOpen]);

    const selectedBatch = options.find(opt => opt.value === value);

    const handleToggle = () => {
        if (!loading && !disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                // ✅ Open karte waqt current selected index set karo
                const currentIndex = options.findIndex(opt => opt.value === value);
                setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
            } else {
                setFocusedIndex(-1);
            }
        }
    };

    const handleSelect = (batch) => {
        onChange(batch);
        setIsOpen(false);
        setFocusedIndex(-1);
        
        // ✅ Selection ke baad button pe focus return (Tab navigation ke liye)
        setTimeout(() => {
            if (buttonRef.current) {
                buttonRef.current.focus();
            }
        }, 50);
    };

    // ✅ Enhanced Keyboard Navigation
    const handleKeyDown = (e) => {
            e.stopPropagation();
        // Dropdown band hai
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
            }
            return;
        }

        // Dropdown khula hai
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const next = prev + 1;
                    return next >= options.length ? 0 : next;  // Wrap to first
                });
                break;

            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const next = prev - 1;
                    return next < 0 ? options.length - 1 : next;  // Wrap to last
                });
                break;

            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < options.length) {
                    handleSelect(options[focusedIndex]);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setFocusedIndex(-1);
                if (buttonRef.current) {
                    buttonRef.current.focus();
                }
                break;

            case 'Home':
                e.preventDefault();
                setFocusedIndex(0);
                break;

            case 'End':
                e.preventDefault();
                setFocusedIndex(options.length - 1);
                break;

            case 'Tab':
                // Tab ke liye focused item select kar do, phir close
                if (focusedIndex >= 0 && focusedIndex < options.length) {
                    e.preventDefault();
                    handleSelect(options[focusedIndex]);
                } else {
                    // No focused item - just close
                    setIsOpen(false);
                    setFocusedIndex(-1);
                }
                // Browser default Tab behavior
                break;

            default:
                break;
        }
    };

    return (
        <div 
            ref={dropdownRef} 
            className="custom-batch-dropdown" 
              data-batch-dropdown="true"
            style={{ position: 'relative' }}
        >
            {/* Dropdown Button */}
            <div 
                ref={buttonRef}
                tabIndex={disabled ? -1 : 0}
                className={`form-control form-control-sm d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                style={{ 
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: disabled ? '#e9ecef' : '#fff',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                <span>
                    {loading ? 'Loading...' : selectedBatch ? selectedBatch.value : 'Select Batch'}
                </span>
                <span>{isOpen ? '▲' : '▼'}</span>
            </div>

            {/* Dropdown Table */}
            {isOpen && !disabled && (
                <div 
                    ref={tableRef}
                    className="batch-dropdown-panel"
                    style={{ 
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        width: '650px',
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        overflowX: 'auto',
                        zIndex: 1050,
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.25rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        marginTop: '2px'
                    }}
                >
                    <table 
                        className="table table-sm table-hover mb-0" 
                        style={{ 
                            fontSize: '0.85rem',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <thead className="table-light sticky-top">
                            <tr>
                                <th style={{ width: '140px', padding: '8px' }}>Batch No</th>
                                <th style={{ width: '100px', padding: '8px' }} className="text-end">MRP</th>
                                <th style={{ width: '100px', padding: '8px' }} className="text-end">Sale Rate</th>
                                <th style={{ width: '120px', padding: '8px' }}>Exp Date</th>
                                <th style={{ width: '100px', padding: '8px' }} className="text-end">Avail Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : options.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted py-3">
                                        No batches available
                                    </td>
                                </tr>
                            ) : (
                                options.map((batch, index) => (
                                    <tr 
                                        key={batch.value}
                                        onClick={() => handleSelect(batch)}
                                        onMouseEnter={() => setFocusedIndex(index)}
                                        style={{ cursor: 'pointer' }}
                                        className={
                                            value === batch.value 
                                                ? 'table-active'
                                                : focusedIndex === index 
                                                    ? 'table-primary'
                                                    : ''
                                        }
                                    >
                                        <td className="fw-bold" style={{ padding: '8px' }}>
                                            {batch.value}
                                        </td>
                                        <td className="text-end" style={{ padding: '8px' }}>
                                            ₹{Number(batch.mrp).toFixed(2)}
                                        </td>
                                        <td className="text-end" style={{ padding: '8px' }}>
                                            ₹{Number(batch.sRate).toFixed(2)}
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            {new Date(batch.expDate).toLocaleDateString('en-GB')}
                                        </td>
  <td className="text-end" style={{ padding: '8px' }}>
    <span 
        style={{ 
            backgroundColor: Number(batch.balance) > 0 ? '#198754' : '#dc3545', // Direct Green/Red Color
            color: '#ffffff',       // Direct White Text
            padding: '4px 8px',     // Spacing
            borderRadius: '4px',    // Rounded corners
            fontSize: '12px',       // Readable font size
            fontWeight: 'bold',     // Bold text
            display: 'inline-block', // Ensure layout
            minWidth: '30px',       // Minimum width
            textAlign: 'center'
        }}
    >
        {batch.balance}
    </span>
</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
});

// ✅ Display name for debugging
BatchDropdown.displayName = 'BatchDropdown';

export default BatchDropdown;
